import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  InputLabel,
  Checkbox,
  Menu,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material/styles";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import GroupChildrenItem from "./GroupChildrenItem";
import UploadErrorDialog from "./UploadErrorDialog";
import {
  CustomTreeItem,
  TypographyLarge,
  TypographyNormal,
  TypographySmall,
} from "../../customComponent";
import {
  CloseConfirmationDialog,
  openCancelDialog,
} from "../../global/CloseConfirmationDialog";
import { inputOnlyNumbers } from "../../../helpers/inputProcessing";
import { logger } from "../../../helpers/logger";
import { SET_DEFAULT_ASSET_BEHAVIOR } from "../../../store/actionTypes/cmsActionType";
import {
  setErrorHandler,
  setLayerMedias,
  uploadMedia,
} from "../../../store/actions/cmsAction";
import {
  GroupFolderOpenTooltipIcon,
  GroupFolderTooltipIcon,
} from "../../SharedComponents";
import MediaPicker from "./MediaPicker";
import Loading from "../../loading/Loading";
import {
  SET_GLOBAL_ERROR,
  SET_GLOBAL_LOADING,
} from "../../../store/actionTypes/globalActionType";
import { getImageSource, splitFileName } from "../../../helpers/fileProcessing";
import { v4 as uuidv4 } from "uuid";
import parser from "html-react-parser";
import update from "immutability-helper";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

const assetMaxSizeLimit =
  (process.env.REACT_APP_ASSET_MAX_SIZE_LIMIT || 300) * 1024 * 1024; // 300MB

const StyledBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  ":last-child": {
    marginBottom: 0,
  },
}));

const MediaContentSetting = ({
  open,
  handleClose,
  container,
  editAsset,
  saveEditChanges,
  assetCount,
}) => {
  const dispatch = useDispatch();
  const { layerMedias, uploadingMedia, allowedAssetFiles } = useSelector(
    (state) => state.cms
  );

  const { t } = useTranslation("cms");
  const { t: tCommon } = useTranslation();

  const [assetIndex, setAssetIndex] = useState(null);
  const [assetDuration, setAssetDuration] = useState(10);
  const [assetFit, setAssetFit] = useState("FIT");
  const [fitToAll, setFitToAll] = useState(false);
  const [editedFlag, setEditedFlag] = useState(false);

  const [errorDurationFlag, setErrorDurationFlag] = useState(false);
  const [groupFlag, setGroupFlag] = useState(false);
  const [childAsset, setChildAsset] = useState(null);
  const [childIndex, setChildIndex] = useState(-1);
  const [assetChildren, setAssetChildren] = useState([]);
  const [childrenEditFlag, setChildrenEditFlag] = useState(false);

  const [appendNewFiles, setAppendNewFiles] = useState([]);
  const [uploadNewFiles, setUploadNewFiles] = useState([]);

  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [fileErrors, setFileErrors] = useState([]);
  const [fileErrorMessages, setFileErrorMessages] = useState([]);

  const [errNoFiles, setErrNoFiles] = useState(false);
  const [errNotUploaded, setErrNotUploaded] = useState(false);
  const [errNoChildren, setErrNoChildren] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);

  const [openMediaPicker, setOpenMediaPicker] = useState(false);
  const [anchorMenuEl, setAnchorMenuEl] = useState(null);
  const openMenu = Boolean(anchorMenuEl);
  const handleOpenMenu = (event) => {
    setAnchorMenuEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorMenuEl(null);
  };

  const handleOpenMediaPicker = () => {
    setOpenMediaPicker(true);
  };

  const handleCloseMediaPicker = () => {
    setOpenMediaPicker(false);
  };

  const {
    acceptedFiles,
    getInputProps,
    open: openSelectFile,
  } = useDropzone({
    accept: {
      "image/*": [],
      "video/*": [],
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (active.id !== over.id) {
      setAssetChildren((children) => {
        const oldIndex = children.findIndex((i) => i.id === active.id);
        const newIndex = children.findIndex((i) => i.id === over.id);
        const updated = arrayMove(children, oldIndex, newIndex);
        setChildrenEditFlag(true);
        setEditedFlag(true);

        const previewIndex = updated.findIndex((i) => i.id === childAsset.id);
        setChildIndex(previewIndex);
        return updated;
      });
    }
  }

  useEffect(() => {
    if (!errorDialogOpen) {
      setFileErrors([]);
      setFileErrorMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorDialogOpen]);

  useEffect(() => {
    if (acceptedFiles?.length > 0) {
      setErrNoFiles(false);
      setErrNotUploaded(false);
      setAppendNewFiles(acceptedFiles);
      setEditedFlag(true);
    } else {
      setErrNoFiles(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedFiles]);

  const parseSubmittedFiles = (files, callback) => {
    const newChildAssets = {};
    const localURLs = [];
    const parsed = [];

    const validFiles = [];
    const errorFiles = [];
    const errorMessages = [];

    const updatedChildren = [...assetChildren];
    const parseImage = async (file, index) => {
      logger.log(`parseImage [${index}]`, file);
      const fileNameSplit = splitFileName(file.name);
      const inputProps = {
        ...fileNameSplit,
        type: file.type,
      };

      await getImageSource({ input: file, inputProps }, allowedAssetFiles).then(
        ({ type, url, dimension, videoURL, localURL, resized, duration }) => {
          const resFile = {
            id: uuidv4(),
            file: file,
            fileSrc: url,
            videoSrc: videoURL,
            type: type,
            name: file.name,
            dimension: dimension,
            duration: duration,
            size: file.size,
          };

          if (resized) {
            resFile.file = resized;
            resFile.name = resized.name;
            resFile.size = resized.size;
          }

          newChildAssets[resFile.id] = true;
          localURLs.push(localURL);
          parsed.push(resFile);

          resFile.needUpload = true;
          updatedChildren.push(resFile);
        },
        ({ file, message }) => {
          logger.error(
            `Error occured when parsing file [${file.name}]:`,
            message
          );
          errorFiles.push(file);
          errorMessages.push(t("uploadError.errNotSupported"));
        }
      );

      if (parsed.length + errorFiles.length === files.length) {
        if (parsed.length > 0) {
          const updated = [...uploadNewFiles, ...parsed];
          setUploadNewFiles(updated);
          logger.log(`uploadNewFiles-updatedChildren:`, updatedChildren);
          setAssetChildren(updatedChildren);
          setChildrenEditFlag(true);
          setEditedFlag(true);

          if (callback) callback();
        }

        if (errorFiles.length > 0) {
          setFileErrors(errorFiles);
          setFileErrorMessages(errorMessages);
          setErrorDialogOpen(true);
        }
      }
    };

    const parseSelectedMedias = async (files) => {
      dispatch({ type: SET_GLOBAL_LOADING, payload: true });

      files.forEach((file) => {
        if (file.size <= assetMaxSizeLimit) {
          validFiles.push(file);
        } else {
          errorFiles.push(file);
          errorMessages.push(t("uploadError.errSizeExceedLimit"));
        }
      });

      for (let i = 0; i < validFiles.length; i++) {
        await parseImage(validFiles[i], i);
      }
      dispatch({ type: SET_GLOBAL_LOADING, payload: false });
    };

    parseSelectedMedias(files);
  };

  useEffect(() => {
    dispatch({ type: SET_GLOBAL_ERROR, payload: false });
    dispatch(setErrorHandler({ limitReached: false }));

    if (appendNewFiles?.length > 0) {
      logger.log(`Grouped-appendNewFiles:`, appendNewFiles);
      parseSubmittedFiles(appendNewFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appendNewFiles]);

  const uploadFiles = async () => {
    logger.log(`uploadFiles:`, { uploadNewFiles });
    dispatch(
      uploadMedia(uploadNewFiles, (res) => {
        logger.log(`uploadNewFiles-RES:`, { res });

        if (res.length > 0) {
          setUploadNewFiles([]);
          setErrNotUploaded(false);
          setLoadingUpload(false);
          setTimeout(() => {
            let updated = [...assetChildren];
            const oldLength = editAsset.children.length;
            res.forEach((asset, index) => {
              updated = update(updated, {
                [oldLength + index]: {
                  $merge: {
                    id: asset.response.id,
                    needUpload: false,
                  },
                },
              });
            });

            logger.log(`upload-assetChildren-updated:`, { updated });
            setAssetChildren(updated);
          }, 1234);
        }
      })
    );
  };

  const addChildrenAsset = (data, order) => {
    if (order.length === 0) {
      dispatch(setErrorHandler({ selectedMedia: true }));
      return;
    }

    let checkIfExists;
    if (order.length > 0) {
      checkIfExists = assetChildren.filter((media) => data[media.id]);
      if (checkIfExists?.length > 0) {
        logger.log(`addContent-checkDuplicate:`, true);
        dispatch(setErrorHandler({ assetDuplicated: true }));
        return;
      }
    }

    const updatedChildren = [...assetChildren];
    order.forEach((newChildID) => {
      updatedChildren.push(data[newChildID]);
    });

    handleCloseMediaPicker();
    setAssetChildren(updatedChildren);
    setChildrenEditFlag(true);
    setErrNotUploaded(false);
    setErrNoChildren(false);
    setEditedFlag(true);
  };

  const removeChildrenAsset = (e, childId, index) => {
    e.stopPropagation();
    const updatedChildren = [...assetChildren].filter((_, i) => i !== index);
    const updatedUpload = [...uploadNewFiles].filter(
      (asset) => asset.id !== childId
    );
    setUploadNewFiles(updatedUpload);

    if (updatedChildren.length === 0) {
      setChildAsset(null);
      setChildIndex(-1);
    }

    if (childIndex !== index) {
      let newIndex = childIndex;
      if (childIndex > index) {
        newIndex = childIndex - 1;
      }
      setChildIndex(newIndex);
    } else {
      if (updatedChildren[index]) {
        setChildAsset(updatedChildren[index]);
      } else {
        let newIndex = index - 1;
        if (newIndex < 0) newIndex = 0;
        if (updatedChildren[newIndex]) {
          setChildAsset(updatedChildren[newIndex]);
          setChildIndex(newIndex);
        }
      }
    }

    setAssetChildren(updatedChildren);
    setChildrenEditFlag(true);
    setEditedFlag(true);
  };

  const handleItemFocus = (event, itemId) => {
    if (assetChildren?.length > 0 && itemId?.includes("_")) {
      const childId = itemId.split("_")[1];
      const childTargetIndex = assetChildren.findIndex(
        (child) => `${child.id}` === childId
      );

      const childTarget = assetChildren[childTargetIndex];
      setChildAsset(childTarget);
      setChildIndex(childTargetIndex);
    }
  };

  useEffect(() => {
    if (open) {
      logger.log(`mediasetting opened!`, { editAsset });
      if (editAsset) {
        const index = layerMedias[container.id].findIndex(
          (media) => media.id === editAsset.id
        );

        setAssetIndex(index);
        setAssetDuration(editAsset.interval);
        setAssetFit(editAsset.assetFit);
        setFitToAll(false);

        if (editAsset.type === "GROUPED") {
          setChildAsset(null);
          setGroupFlag(true);
          setAssetChildren(editAsset.children);
        }
      } else {
        setAssetChildren([]);
        setAppendNewFiles([]);
        setUploadNewFiles([]);
        setChildrenEditFlag(false);
        setErrorDurationFlag(false);
      }
    } else {
      setErrorDurationFlag(false);
      setErrNoFiles(false);
      setErrNotUploaded(false);
      setErrNoChildren(false);
      setOpenMediaPicker(false);
      setEditedFlag(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleAssetDuration = (e) => {
    setErrorDurationFlag(false);
    const value = e.target.value;
    setAssetDuration(value);
    setEditedFlag(true);
  };

  const handleAssetFit = (e) => {
    const value = e.target.value;
    setAssetFit(value);
    setEditedFlag(true);
  };

  const handleCloseDialog = async () => {
    handleClose();
  };

  const handleOpenCancelDialog = () => {
    openCancelDialog(dispatch, editedFlag, handleCloseDialog);
  };

  const handleSaveEdit = async () => {
    const edited = {
      interval: parseInt(assetDuration),
      assetFit: assetFit,
    };

    if (childrenEditFlag) {
      if (assetChildren.length < 2) {
        setErrNoChildren(true);
        return;
      }

      if (uploadNewFiles.length > 0) {
        setLoadingUpload(true);
        await uploadFiles();
        return;
      }

      edited.children = assetChildren;
      edited.updated = true;
    }

    logger.log(`handleSaveEdit:`, { edited, errNoFiles, errNotUploaded });
    saveEditChanges(assetIndex, edited);
    handleClose();
  };

  const submitChanges = () => {
    if (parseInt(assetDuration) === 0) {
      setErrorDurationFlag(true);
      return;
    }

    handleSaveEdit();
    if (fitToAll) {
      logger.log(`setAssetBehaviorDefault!`, layerMedias);
      const layerIDs = Object.keys(layerMedias);
      let updatedMedias = { ...layerMedias };

      layerIDs.forEach((layerID) => {
        const assets = layerMedias[layerID];
        for (let i = 0; i < assets.length; i++) {
          updatedMedias[layerID][i].assetFit = assetFit;
        }
      });

      dispatch(setLayerMedias({ forceUpdate: { ...updatedMedias } }));
      dispatch({ type: SET_DEFAULT_ASSET_BEHAVIOR, payload: assetFit });
    }
  };

  const setAssetBehaviorDefault = () => {
    setFitToAll(!fitToAll);
    setEditedFlag(true);
  };

  const parseContentSubmitText = useMemo(() => {
    if (!editAsset) {
      return tCommon("submitBtn");
    } else {
      if (uploadNewFiles.length > 0) {
        return tCommon("submitBtn");
      }
      return tCommon("saveBtn");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editAsset, uploadNewFiles]);

  const parseObjectFit = (assetFit) => {
    switch (assetFit) {
      default:
      case "FIT":
        return "contain";
      case "STRETCH":
        return "fill";
    }
  };

  const previewElement = useMemo(() => {
    if (!editAsset) {
      return;
    }

    const targetAsset = childAsset ?? editAsset;
    let mediaSrc = targetAsset.videoSrc ?? targetAsset.fileSrc;

    const targetType = targetAsset?.type?.toUpperCase();
    switch (targetType) {
      case "VIDEO":
        if (!mediaSrc) mediaSrc = targetAsset.url;
        return (
          <video
            is="x-muted"
            draggable={false}
            src={mediaSrc}
            alt={targetAsset.name}
            controls={true}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              minHeight: "100%",
              objectFit: parseObjectFit(assetFit),
            }}
          />
        );

      case "IMAGE":
        if (!mediaSrc) mediaSrc = targetAsset.thumbnailUrl;
        return (
          <img
            draggable={false}
            src={mediaSrc}
            alt={t("contentImageAltText")}
            onClick={(e) => e.stopPropagation()}
            loading="lazy"
            style={{
              width: "100%",
              objectFit: parseObjectFit(assetFit),
            }}
          />
        );

      default:
        return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editAsset, childAsset, assetFit]);

  const enableDuration = useMemo(() => {
    if (assetCount > 1) {
      return true;
    } else if (assetCount === 1) {
      return !editAsset ? true : false;
    } else {
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetCount, editAsset]);

  return (
    <Dialog open={open} maxWidth="lg" fullWidth disableEscapeKeyDown={true}>
      <CloseConfirmationDialog handleCancel={handleCloseDialog} />

      <DialogTitle
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        {t("setting.mediaTitle")}
        <IconButton onClick={() => handleOpenCancelDialog()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ minHeight: "33vh" }}>
        <Loading />

        <UploadErrorDialog
          open={errorDialogOpen}
          onClose={() => setErrorDialogOpen(false)}
          files={fileErrors}
          errors={fileErrorMessages}
        />

        <MediaPicker
          open={openMediaPicker}
          handleClose={handleCloseMediaPicker}
          name={``}
          addContent={addChildrenAsset}
          container={container}
          contentState={[null]}
          noUpload={true}
        />

        <Box
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          sx={{ mt: 3, px: 3 }}
        >
          <Box
            display={"flex"}
            alignItems={"center"}
            sx={{ p: 4, width: "100%" }}
          >
            <Box
              display={"flex"}
              justifyContent={"left"}
              alignItems={"center"}
              sx={{
                py: 2,
                px: 4,
                width: "100%",
                border: "1px solid black",
                borderRadius: "22px",
              }}
            >
              <Box
                width={"50%"}
                display={"flex"}
                flexDirection={"column"}
                gap={1}
              >
                {groupFlag && (
                  <StyledBox>
                    <Box display={"flex"} gap={"3px"} alignItems={"center"}>
                      <TypographyLarge sx={{ ml: 1 }}>
                        {t("setting.groupedMediaLabel")}
                      </TypographyLarge>
                    </Box>

                    <Box
                      width={"90%"}
                      display={"flex"}
                      flexDirection={"column"}
                      alignItems={"center"}
                      gap={1}
                    >
                      <SimpleTreeView
                        disableSelection={
                          assetChildren?.length > 0 ? false : true
                        }
                        onItemFocus={
                          assetChildren?.length > 0
                            ? handleItemFocus
                            : undefined
                        }
                        defaultExpandedItems={[`${editAsset.assetID}`]}
                      >
                        <CustomTreeItem
                          itemId={`${editAsset.assetID}`}
                          ExpandIcon={GroupFolderTooltipIcon}
                          CollapseIcon={GroupFolderOpenTooltipIcon}
                          customRootStyle={{
                            sx: {
                              position: "relative",
                            },
                          }}
                          label={
                            <Tooltip
                              arrow
                              disableInteractive
                              title={editAsset.name}
                            >
                              <Box sx={{ width: "250px" }}>
                                <TypographyNormal
                                  sx={{
                                    fontSize: "15px",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {editAsset.name}
                                </TypographyNormal>
                              </Box>
                            </Tooltip>
                          }
                          sx={{ maxWidth: "300px" }}
                        >
                          {assetChildren?.length > 0 ? (
                            <DndContext
                              sensors={sensors}
                              collisionDetection={closestCenter}
                              onDragEnd={handleDragEnd}
                            >
                              <SortableContext
                                items={assetChildren.map((i) => i.id)}
                                strategy={verticalListSortingStrategy}
                              >
                                {assetChildren.map((child, index) => (
                                  <GroupChildrenItem
                                    key={child.id}
                                    asset={editAsset}
                                    childAsset={child}
                                    index={index}
                                    uploadingMedia={uploadingMedia}
                                    removeAsset={removeChildrenAsset}
                                  />
                                ))}
                              </SortableContext>
                            </DndContext>
                          ) : (
                            <CustomTreeItem
                              key={`null`}
                              itemId={`null`}
                              customRootStyle={{
                                sx: {
                                  position: "relative",
                                },
                              }}
                              customContentStyle={{
                                sx: {
                                  ml: "16px",
                                  pl: "16px",
                                  pr: "8px",
                                  backgroundColor: "unset",
                                  cursor: "unset",
                                  ":hover": {
                                    backgroundColor: "unset",
                                  },
                                },
                              }}
                              label={
                                <Box sx={{ width: "250px" }}>
                                  <TypographyNormal
                                    sx={{
                                      fontSize: "15px",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {t("setting.noChildrenAssetName")}
                                  </TypographyNormal>
                                </Box>
                              }
                            />
                          )}
                        </CustomTreeItem>
                      </SimpleTreeView>

                      <Box display={"flex"} alignItems={"center"}>
                        <input {...getInputProps()} />
                        <Box sx={{ ml: 2 }}>
                          <Tooltip
                            arrow
                            disableInteractive
                            title={t("setting.addChildrenAssetTip")}
                          >
                            <Button
                              sx={{
                                width: "60px",
                                minWidth: "60px",
                                height: "30px",
                                borderRadius: "16px",
                                boxShadow:
                                  "0px 10px 10px -10px rgba(0,0,0,0.4)",
                              }}
                              variant="contained"
                              onClick={handleOpenMenu}
                            >
                              <AddIcon fontSize="small" />
                            </Button>
                          </Tooltip>
                          <Menu
                            id="basic-menu"
                            anchorEl={anchorMenuEl}
                            open={openMenu}
                            onClose={handleCloseMenu}
                            elevation={4}
                            anchorOrigin={{
                              horizontal: "left",
                              vertical: "bottom",
                            }}
                            transformOrigin={{
                              horizontal: "left",
                              vertical: "top",
                            }}
                            sx={{ ml: 1 }}
                          >
                            <MenuItem
                              onClick={() => {
                                openSelectFile();
                                handleCloseMenu();
                              }}
                              sx={{
                                display: "flex",
                              }}
                            >
                              <TypographyNormal sx={{ ml: 1 }}>
                                {t("setting.placeholderUploadNew")}
                              </TypographyNormal>
                            </MenuItem>

                            <MenuItem
                              onClick={() => {
                                handleOpenMediaPicker();
                                handleCloseMenu();
                              }}
                              sx={{
                                display: "flex",
                              }}
                            >
                              <TypographyNormal sx={{ ml: 1 }}>
                                {t("setting.placeholderExistingMedia")}
                              </TypographyNormal>
                            </MenuItem>
                          </Menu>
                        </Box>
                      </Box>
                    </Box>
                  </StyledBox>
                )}

                <StyledBox>
                  <Box display={"flex"} gap={"3px"} alignItems={"center"}>
                    <TypographyLarge sx={{ ml: 1 }}>
                      {t("setting.durationMediaLabel")}
                    </TypographyLarge>
                    <Tooltip
                      arrow
                      disableInteractive
                      placement="top"
                      title={
                        <TypographySmall
                          sx={{ color: "white", textAlign: "center" }}
                        >
                          {t("setting.durationMediaTip")}
                        </TypographySmall>
                      }
                    >
                      <InfoOutlinedIcon fontSize="10px" />
                    </Tooltip>
                  </Box>

                  <Box width={"70%"} display={"flex"} flexDirection={"column"}>
                    <Box
                      width={"100%"}
                      display={"flex"}
                      alignItems={"center"}
                      gap={1}
                    >
                      <Tooltip
                        arrow
                        disableInteractive
                        title={
                          enableDuration ? (
                            ""
                          ) : (
                            <TypographySmall
                              sx={{ color: "white", textAlign: "center" }}
                            >
                              {t("setting.durationDisabledTip")}
                            </TypographySmall>
                          )
                        }
                      >
                        <TextField
                          margin="dense"
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={assetDuration}
                          disabled={!enableDuration}
                          onChange={(e) => handleAssetDuration(e)}
                          onKeyDown={(e) => inputOnlyNumbers(e)}
                          sx={{ pl: 2 }}
                        />
                      </Tooltip>
                      <TypographyNormal flex={"none"}>
                        {t("setting.durationHint")}
                      </TypographyNormal>
                    </Box>

                    {errorDurationFlag && (
                      <TypographySmall
                        sx={{
                          ml: 2,
                          mt: "2px",
                          color: "red",
                          fontWeight: 600,
                        }}
                      >
                        {t("setting.durationErrorHint")}
                      </TypographySmall>
                    )}
                  </Box>
                </StyledBox>

                <StyledBox>
                  <Box display={"flex"} gap={"3px"} alignItems={"center"}>
                    <TypographyLarge sx={{ ml: 1 }}>
                      {t("setting.assetBehaviorLabel")}
                    </TypographyLarge>
                    <Tooltip
                      arrow
                      disableInteractive
                      placement="top"
                      title={
                        <TypographySmall
                          sx={{ color: "white", textAlign: "center" }}
                        >
                          {t("setting.assetBehaviorTip")}
                        </TypographySmall>
                      }
                    >
                      <InfoOutlinedIcon fontSize="10px" />
                    </Tooltip>
                  </Box>

                  <Box width={"100%"} display={"flex"} alignItems={"center"}>
                    <Box
                      display={"flex"}
                      flexDirection={"column"}
                      sx={{ pl: 2 }}
                    >
                      <TextField
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        size="small"
                        select
                        SelectProps={{
                          native: true,
                        }}
                        value={assetFit}
                        onChange={(e) => {
                          handleAssetFit(e);
                        }}
                        sx={{ my: 1, width: "222px" }}
                      >
                        <option value={"FIT"} key={"fit"}>
                          {t("setting.assetBehaviorFit")}
                        </option>
                        <option value={"STRETCH"} key={"stretch"}>
                          {t("setting.assetBehaviorStretch")}
                        </option>
                      </TextField>

                      <Box display={"flex"} alignItems={"center"}>
                        <Checkbox
                          value={fitToAll}
                          checked={fitToAll}
                          size="small"
                          onClick={() => setAssetBehaviorDefault()}
                          sx={{ padding: "4px" }}
                        />
                        <InputLabel
                          sx={{ cursor: "pointer" }}
                          onClick={() => setAssetBehaviorDefault()}
                        >
                          <TypographyNormal>
                            {t("setting.assetBehaviorSetDefaultBtn")}
                          </TypographyNormal>
                        </InputLabel>
                        <Tooltip
                          arrow
                          disableInteractive
                          placement="top"
                          title={
                            <TypographySmall
                              sx={{ color: "white", textAlign: "center" }}
                            >
                              {t("setting.assetBehaviorSetDefaultTip")}
                            </TypographySmall>
                          }
                        >
                          <InfoOutlinedIcon
                            fontSize="1rem"
                            sx={{ marginLeft: "3px" }}
                          />
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                </StyledBox>
              </Box>

              <Box width={"50%"} display={"flex"} flexDirection={"column"}>
                <Box width={"100%"} sx={{ py: 1, px: 2 }}>
                  <Box sx={{ mb: 1, ml: 1 }}>
                    <TypographyLarge>
                      {t("setting.previewLabel")}
                    </TypographyLarge>
                    {groupFlag && childAsset?.id && (
                      <Tooltip arrow disableInteractive title={childAsset.name}>
                        <TypographyNormal
                          sx={{
                            mx: 1,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {childAsset
                            ? `${childIndex + 1}. ${childAsset.name}`
                            : t("setting.selectGroupChildPreviewHint")}
                        </TypographyNormal>
                      </Tooltip>
                    )}
                  </Box>
                  <Box
                    width={"100%"}
                    display={"flex"}
                    alignContent={"center"}
                    sx={{
                      height: "250px",
                      backgroundColor: "black",
                    }}
                  >
                    {previewElement}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {errNoChildren && (
        <TypographySmall
          sx={{
            mr: 3,
            mt: "2px",
            color: "red",
            fontWeight: 600,
            textAlign: "right",
          }}
        >
          {parser(t("setting.noChildrenInGroupError"))}
        </TypographySmall>
      )}

      <DialogActions sx={{ mx: 2, mb: 1 }}>
        <>
          <Button
            variant="outlined"
            disabled={loadingUpload}
            onClick={() => handleOpenCancelDialog()}
          >
            {tCommon("cancelBtn")}
          </Button>
          <Button
            variant="contained"
            disabled={loadingUpload}
            onClick={() => submitChanges()}
          >
            {parseContentSubmitText}
          </Button>
        </>
      </DialogActions>
    </Dialog>
  );
};

export default MediaContentSetting;
